import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { backendServices } from '..';

/**
 * Custom hook to fetch countries data (sorted by index).
 * @returns React Query hook result.
 */
export function useCountries() {
    const { isLoading, error, data, isFetching, status } = useQuery(
        ['Country'],
        async () => {
            const api = new backendServices.Apis.CommonApi();

            const res = await api.getCountry({});

            return res.data;
        },
        {
            staleTime: Infinity,
            cacheTime: Infinity,
        }
    );

    return {
        isLoading: isLoading || isFetching,
        error,
        data,
        status,
    };
}

/**
 * Custom hook to fetch country and state options.
 * @param selectedCountryCode - The selected country code.
 * @param countryList - The list of countries.
 * @returns Country and state options.
 */
export function useCountryAndStateOptions({
    selectedCountryCode,
    countryList,
}: {
    selectedCountryCode: string | null;
    countryList: backendServices.ViewModels.CountryCodeViewModel[];
}) {
    const countryOptions = useMemo<Record<string, string>>(() => {
        const entries = countryList.map(({ Name: key, Code: value }) => [key, value]);

        return Object.fromEntries(entries);
    }, [countryList]);

    const stateOptions = useMemo<Record<string, string>>(() => {
        const stateList = countryList.find((c) => c.Code === selectedCountryCode)?.StateCodes ?? [];

        const entries = stateList.map(({ Name: key, Code: value }) => [key, value]);

        return Object.fromEntries(entries);
    }, [countryList, selectedCountryCode]);

    return {
        countryOptions,
        stateOptions,
    };
}
